/**
 * Share history hooks (React Query)
 *
 * The share links created for one project. The query passes its AbortSignal
 * to the request, so unmounting the panel mid-load cancels it. Revoking
 * refetches the list whether the revoke succeeds or fails.
 */

import {
	useMutation,
	useMutationState,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { queryKeys } from "@/lib/query-keys";
import {
	getProjectShareLinks,
	revokeShareLink,
} from "../services/share-links-api.service";

export function useProjectShareHistory(projectId: string) {
	return useQuery({
		queryKey: queryKeys.sharing.projectHistory(projectId),
		queryFn: ({ signal }) => getProjectShareLinks(projectId, { signal }),
		// Links are shared from other tabs, which do not invalidate this list, so
		// it refetches whenever the panel mounts.
		staleTime: 0,
	});
}

const revokeKey = (projectId: string) =>
	[...queryKeys.sharing.projectHistory(projectId), "revoke"] as const;

export function useRevokeShareLink(projectId: string) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: revokeKey(projectId),
		mutationFn: (shareLinkId: string) => revokeShareLink(shareLinkId),
		// On the hook, not per `mutate` call: per-call callbacks fire only for
		// the latest call, so a second quick revoke would swallow the first toast.
		onSuccess: () => toast.success(t("sharing.revokeSucceeded")),
		onError: () => toast.error(t("sharing.revokeFailed")),
		onSettled: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.sharing.projectHistory(projectId),
			}),
	});
}

/** The ids of every share link with a revoke still in flight, not only the latest. */
export function usePendingRevokeIds(projectId: string): string[] {
	return useMutationState({
		filters: { mutationKey: revokeKey(projectId), status: "pending" },
		select: (mutation) => mutation.state.variables as string,
	});
}
